/**
 * The balloon. One per house on the map: the chosen figure, optionally a second
 * figure or the energy label in the label's own colour, on a pill with a thin
 * border, and - if asked - one small line of text underneath.
 *
 * Plain DOM rather than React: these are created and thrown away by the hundred
 * every time the map settles, and MapLibre wants an element, not a component.
 */
import {
  buurtNaam,
  ENERGIELABEL_ONBEKEND_KLEUR,
  energielabelKleur,
  formatAantal,
  formatEuroCompact,
  KANSJE,
  pc4Van,
} from '@mamimap/shared';
import type { KaartFeature } from '@/lib/api/schemas';
import { EENHEID_TEKEN, type Eenheid, formatMaat, prijsPerEenheid, vanM2 } from '@/lib/eenheden';
import type { Kaartinstellingen } from '@/lib/instellingen';

export type BallonEigenschappen = KaartFeature['properties'];

/**
 * Een kansje draagt een sterretje op zijn ballon (MA-39): altijd, los van de
 * markeringen in het weergavepaneel, want een oordeel is een eigen besluit en
 * hoort niet achter een vinkje. Op de kaart en in 3D dezelfde regel.
 */
export function isKansje(eigenschappen: Pick<BallonEigenschappen, 'beoordeling'>): boolean {
  return eigenschappen.beoordeling === KANSJE;
}

/**
 * Opnieuw aangeboden (MA-45): de makelaar zette dezelfde woning met een nieuwe
 * advertentie terug op Funda, vaak voor minder. Net als het sterretje altijd
 * zichtbaar, los van de markeringen: het is wat je het eerst wilt weten.
 */
export function isOpnieuwAangeboden(
  eigenschappen: Pick<BallonEigenschappen, 'opnieuwAangeboden'>,
): boolean {
  return eigenschappen.opnieuwAangeboden === true;
}

/** Wat een schermlezer over de ballon zegt: het adres, en wat erbij hoort. */
export function ballonOmschrijving(
  eigenschappen: Pick<BallonEigenschappen, 'adres' | 'beoordeling' | 'opnieuwAangeboden'>,
): string {
  const delen = [eigenschappen.adres];
  if (isKansje(eigenschappen)) delen.push('kansje');
  if (isOpnieuwAangeboden(eigenschappen)) delen.push('opnieuw aangeboden');
  return delen.join(', ');
}

/**
 * De namen van de keuzes noemen de eenheid die eronder staat: wie op vierkante
 * voet staat ziet 'Prijs/ft² + label', want dat is wat de ballon dan toont.
 */
export function ballonWaardeLabels(eenheid: Eenheid): Record<Kaartinstellingen['ballon'], string> {
  const maat = EENHEID_TEKEN[eenheid];
  return {
    prijsPerM2Label: `Prijs/${maat} + ${maat} + label`,
    prijsPerM2: `Alleen prijs/${maat}`,
    vraagprijs: `Vraagprijs + ${maat}`,
    oppervlakte: 'Oppervlakte',
    prijsPerM2Oppervlakte: `Prijs/${maat} + ${maat}`,
  };
}

export const VORM_LABELS: Record<Kaartinstellingen['vorm'], string> = {
  pil: 'Pilletje',
  rond: 'Rondje',
  druppel: 'Druppelpin',
  blok: 'Blokje',
};

export function kleurLabels(eenheid: Eenheid): Record<Kaartinstellingen['kleur'], string> {
  return {
    energielabel: 'Energielabel',
    prijsPerM2: `Prijs per ${EENHEID_TEKEN[eenheid]}`,
    bouwjaar: 'Bouwjaar',
    woningtype: 'Woningtype',
    geen: 'Geen',
  };
}

export const TEKST_LABELS: Record<Kaartinstellingen['tekst'], string> = {
  geen: 'Niets',
  straat: 'Straatnaam',
  buurt: 'Buurt',
  oppervlakte: 'Oppervlakte',
};

export type Markering = keyof Kaartinstellingen['markeringen'];

export const MARKERING_LABELS: Record<Markering, string> = {
  nieuw: 'Nieuw deze week',
  prijsverlaging: 'Prijsverlaging',
  zonderLabel: 'Zonder energielabel',
  zonderCoordinaat: 'Zonder coördinaat',
  eerderActief: 'Eerder actief geweest',
};

export const LAAG_LABELS: Record<keyof Kaartinstellingen['lagen'], string> = {
  woningen: 'Woningen (Funda)',
  clustering: 'Clustering',
  percelen: 'Kadastrale percelen',
  luchtfoto: 'Luchtfoto',
  passanten: 'Passanten (Amsterdam)',
  bestemmingsplan: 'Bestemmingsplan',
  labels: 'Nieuwe energielabels',
};

/**
 * De ballon is ontworpen op 12 px; de schuifregelaar schaalt daar omheen. De
 * binnenruimte loopt in `em` mee (zie `.ballon-waarde` in index.css), dus een
 * stap in de letter is meteen een stap in de ballon.
 */
export function lettergrootte(grootte: number): string {
  return `${Math.round(12 * grootte * 2) / 2}px`;
}

/** Ink that stays readable on a given fill - WCAG relative luminance, nothing fancier. */
const INKT_DONKER = '#1b1a17';
const INKT_LICHT = '#ffffff';

function kanaal(waarde: number): number {
  const deel = waarde / 255;
  return deel <= 0.03928 ? deel / 12.92 : ((deel + 0.055) / 1.055) ** 2.4;
}

export function relatieveHelderheid(hex: string): number {
  const schoon = hex.replace('#', '');
  const volledig =
    schoon.length === 3
      ? schoon
          .split('')
          .map((teken) => teken + teken)
          .join('')
      : schoon;
  const rood = Number.parseInt(volledig.slice(0, 2), 16);
  const groen = Number.parseInt(volledig.slice(2, 4), 16);
  const blauw = Number.parseInt(volledig.slice(4, 6), 16);
  if (!Number.isFinite(rood) || !Number.isFinite(groen) || !Number.isFinite(blauw)) return 0;
  return 0.2126 * kanaal(rood) + 0.7152 * kanaal(groen) + 0.0722 * kanaal(blauw);
}

/**
 * Het labeldeel draagt de labelkleur - maar als vulling, niet als letterkleur.
 * Geel (C, het grootste blok van deze set) en amber (D) haalden op wit een
 * contrast van ongeveer 1,2:1; als vlak eronder haalt dezelfde kleur er veertien.
 */
export function inktOp(achtergrond: string): string {
  const helderheid = relatieveHelderheid(achtergrond);
  const opDonker = (helderheid + 0.05) / (relatieveHelderheid(INKT_DONKER) + 0.05);
  const opLicht = 1.05 / (helderheid + 0.05);
  return opDonker >= opLicht ? INKT_DONKER : INKT_LICHT;
}

function contrastTussen(a: string, b: string): number {
  const eerste = relatieveHelderheid(a);
  const tweede = relatieveHelderheid(b);
  return (Math.max(eerste, tweede) + 0.05) / (Math.min(eerste, tweede) + 0.05);
}

function kanalen(hex: string): [number, number, number] {
  const schoon = hex.replace('#', '');
  const volledig =
    schoon.length === 3
      ? schoon
          .split('')
          .map((teken) => teken + teken)
          .join('')
      : schoon;
  return [
    Number.parseInt(volledig.slice(0, 2), 16) || 0,
    Number.parseInt(volledig.slice(2, 4), 16) || 0,
    Number.parseInt(volledig.slice(4, 6), 16) || 0,
  ];
}

function schuif(hex: string, naar: number, deel: number): string {
  const verschoven = kanalen(hex).map((waarde) =>
    Math.round(Math.min(255, Math.max(0, waarde + (naar - waarde) * deel))),
  );
  return `#${verschoven.map((waarde) => waarde.toString(16).padStart(2, '0')).join('')}`;
}

/** WCAG AA voor gewone tekst; de ballon is klein, dus daar houden we hem op. */
const MINIMUM_CONTRAST = 4.5;

/**
 * Vlak en inkt voor het labeldeel. De labelkleur is het vlak; blijft de letter er
 * met geen van beide inkten leesbaar op - dat overkomt de middentonen, A+ en E -
 * dan schuift het vlak in stapjes van de inkt vandaan tot het wel haalt. Zes
 * procent per stap, dus het vlak blijft de kleur die de legenda laat zien.
 */
export function labelVulling(kleur: string): { vlak: string; inkt: string } {
  const inkt = inktOp(kleur);
  const naar = inkt === INKT_DONKER ? 255 : 0;
  let vlak = kleur;
  for (let stap = 0; stap < 12 && contrastTussen(vlak, inkt) < MINIMUM_CONTRAST; stap += 1) {
    vlak = schuif(vlak, naar, 0.06);
  }
  return { vlak, inkt };
}

/** Five tints of the single accent, dark = expensive. Used when colour means price. */
export const PRIJSSCHAAL = ['#cfdbe1', '#a6bcc6', '#6f97a6', '#3d6f83', '#133748'] as const;

/** Bouwjaar over dezelfde neutrale schaal: licht is oud, donker is nieuw. */
export const BOUWJAARSCHAAL = ['#e2dcce', '#c6bda6', '#a3987c', '#7b7157', '#524a35'] as const;
export const BOUWJAAR_MIN = 1900;
export const BOUWJAAR_MAX = 2000;

/** Woningtype is geen schaal maar een indeling; twee kleuren die iets betekenen. */
export const WONINGTYPE_KLEUREN: Record<string, string> = {
  appartement: '#3d6f83',
  huis: '#8c2f20',
};
const WONINGTYPE_OVERIG = '#86837a';

/**
 * Het woningtype. De API kan het als veld meesturen; doet zij dat niet, dan staat
 * het in de bron-URL van het portaal zelf (`.../appartement-ruyschstraat-357/…`).
 * Dat is de bron die het opschrijft, niet iets wat wij erbij verzinnen.
 */
export function woningtypeVan(eigenschappen: BallonEigenschappen): string | null {
  const veld = eigenschappen.woningtype;
  if (typeof veld === 'string' && veld.trim() !== '') return veld.trim().toLowerCase();
  const uitUrl = /\/([a-z]+)-[a-z0-9-]+\/\d+/.exec(eigenschappen.bronUrl ?? '');
  return uitUrl?.[1]?.toLowerCase() ?? null;
}

/** De straatnaam: het adres zonder het huisnummer erachter. */
export function straatnaamVan(adres: string): string {
  return adres.replace(/\s+\d+\S*(\s+[a-zA-Z0-9-]{1,4})?$/, '').trim() || adres;
}

/** De buurt: de PC4 uit de postcode, met de naam waaronder mensen hem kennen. */
export function buurtVan(eigenschappen: BallonEigenschappen): string | null {
  const pc4 = pc4Van(eigenschappen.postcode ?? null);
  if (pc4 === null) return null;
  return buurtNaam(pc4) ?? pc4;
}

export type Kleurgever = (eigenschappen: BallonEigenschappen) => string | null;

/**
 * Colour by price runs over the same fixed range as a cluster (see
 * `CLUSTERSCHAAL_MIN`/`MAX`), never over quantiles of whatever is in view. A
 * balloon and the cluster beside it then mean the same thing by the same tint,
 * and the legend can name the amounts instead of "low" and "high".
 */
export function maakKleurgever(kleur: Kaartinstellingen['kleur']): Kleurgever {
  if (kleur === 'geen') return () => null;
  if (kleur === 'energielabel') {
    return (eigenschappen) =>
      eigenschappen.energielabel === null ? null : energielabelKleur(eigenschappen.energielabel);
  }
  if (kleur === 'bouwjaar') {
    return (eigenschappen) => {
      const jaar = eigenschappen.bouwjaar;
      if (typeof jaar !== 'number' || !Number.isFinite(jaar)) return null;
      const deel = (jaar - BOUWJAAR_MIN) / (BOUWJAAR_MAX - BOUWJAAR_MIN);
      const stap = Math.min(
        BOUWJAARSCHAAL.length - 1,
        Math.max(0, Math.floor(deel * BOUWJAARSCHAAL.length)),
      );
      return BOUWJAARSCHAAL[stap] ?? null;
    };
  }
  if (kleur === 'woningtype') {
    return (eigenschappen) => {
      const type = woningtypeVan(eigenschappen);
      if (type === null) return null;
      return WONINGTYPE_KLEUREN[type] ?? WONINGTYPE_OVERIG;
    };
  }
  return (eigenschappen) => {
    const stap = clusterStap(eigenschappen.prijsPerM2);
    return stap === null ? null : (PRIJSSCHAAL[stap] ?? null);
  };
}

/** Toont deze ballonvorm het energielabel als eigen vlak? Alleen de standaard doet dat. */
export function toontLabel(welke: Kaartinstellingen['ballon']): boolean {
  return welke === 'prijsPerM2Label';
}

export function ballonWaarde(
  eigenschappen: BallonEigenschappen,
  welke: Kaartinstellingen['ballon'],
  eenheid: Eenheid = 'm2',
): string {
  switch (welke) {
    case 'vraagprijs':
      return formatEuroCompact(eigenschappen.vraagprijs);
    case 'oppervlakte':
      return formatMaat(eigenschappen.oppervlakteM2, eenheid);
    default:
      return eigenschappen.prijsPerM2 === null
        ? '–'
        : formatEuroCompact(prijsPerEenheid(eigenschappen.prijsPerM2, eenheid));
  }
}

/**
 * De oppervlakte als tweede vlak in de ballon (MA-45): alleen het getal, zonder
 * "m²" erachter - het vlak zelf zegt al wat het is, en zo blijft de ballon
 * smal. Het vette cijfer ervoor blijft het hoofdgetal. Onbekend laat het vlak
 * weg; alleen 'prijs/m² + m²' toont dan een streepje, want daar is hij gekozen.
 */
export function ballonTweedeWaarde(
  eigenschappen: BallonEigenschappen,
  welke: Kaartinstellingen['ballon'],
  eenheid: Eenheid = 'm2',
): string | null {
  const oppervlakte = eigenschappen.oppervlakteM2;
  if (welke === 'prijsPerM2Oppervlakte' && oppervlakte === null) return '–';
  if (welke !== 'prijsPerM2Oppervlakte' && welke !== 'prijsPerM2Label' && welke !== 'vraagprijs') {
    return null;
  }
  if (oppervlakte === null) return null;
  return formatAantal(vanM2(oppervlakte, eenheid));
}

/** Het regeltje onder de ballon, of niets. */
export function tekstOnder(
  eigenschappen: BallonEigenschappen,
  welke: Kaartinstellingen['tekst'],
  eenheid: Eenheid = 'm2',
): string | null {
  switch (welke) {
    case 'straat':
      return straatnaamVan(eigenschappen.adres);
    case 'buurt':
      return buurtVan(eigenschappen);
    case 'oppervlakte':
      return eigenschappen.oppervlakteM2 === null
        ? null
        : formatMaat(eigenschappen.oppervlakteM2, eenheid);
    default:
      return null;
  }
}

/**
 * Elke markering die een woning kan dragen is één bit. Zo is 'valt deze woning
 * binnen een van de aangezette markeringen' één en-bewerking, en kan een cluster
 * zijn woningen samenvatten als zestien tellingen in plaats van als negenhonderd
 * rijen die het weer moet nalopen zodra je een vinkje verzet.
 *
 * `zonderCoordinaat` heeft geen bit: die woningen staan per definitie niet op de
 * kaart, dus er is niets om te markeren.
 */
export const MARKERING_BITS: Record<Markering, number> = {
  nieuw: 1,
  prijsverlaging: 2,
  zonderLabel: 4,
  zonderCoordinaat: 0,
  eerderActief: 8,
};

/** Hoeveel verschillende maskers er zijn: vier bits, dus zestien bakjes. */
export const MASKERS = 16;

export function markeringMasker(eigenschappen: Partial<BallonEigenschappen>): number {
  const badge = (eigenschappen.badge ?? '').toLowerCase();
  let masker = 0;
  if (eigenschappen.energielabel === null) masker |= MARKERING_BITS.zonderLabel;
  if (badge.includes('nieuw')) masker |= MARKERING_BITS.nieuw;
  if (eigenschappen.prijsVerlaagd === true || badge.includes('verlaag')) {
    masker |= MARKERING_BITS.prijsverlaging;
  }
  if ((eigenschappen.terugkomsten ?? 0) > 0) masker |= MARKERING_BITS.eerderActief;
  return masker;
}

/** Welke bits het paneel op dit moment aan heeft staan. */
export function gekozenMasker(markeringen: Kaartinstellingen['markeringen']): number {
  let masker = 0;
  for (const [naam, bit] of Object.entries(MARKERING_BITS) as [Markering, number][]) {
    if (markeringen[naam]) masker |= bit;
  }
  return masker;
}

/** Valt deze woning binnen een van de aangezette markeringen? */
export function isGemarkeerd(
  eigenschappen: BallonEigenschappen,
  markeringen: Kaartinstellingen['markeringen'],
): boolean {
  return (markeringMasker(eigenschappen) & gekozenMasker(markeringen)) !== 0;
}

/** Hoeveel woningen in deze verzameling binnen de aangezette markeringen vallen. */
export function telGemarkeerd(
  features: readonly { properties: BallonEigenschappen }[],
  markeringen: Kaartinstellingen['markeringen'],
): number {
  const gekozen = gekozenMasker(markeringen);
  if (gekozen === 0) return 0;
  let aantal = 0;
  for (const feature of features) {
    if ((markeringMasker(feature.properties) & gekozen) !== 0) aantal += 1;
  }
  return aantal;
}

/**
 * Welke keuzes de gegevens van dit moment kunnen dragen. Een keuze die nergens op
 * slaat hoort uit te staan met de reden erbij, niet stil niets te doen. Komt het
 * veld er later bij, dan gaat de knop vanzelf aan.
 */
export type Beschikbaar = {
  bouwjaar: boolean;
  woningtype: boolean;
  buurt: boolean;
  nieuw: boolean;
  prijsverlaging: boolean;
  eerderActief: boolean;
};

export function beschikbaarheid(features: readonly KaartFeature[]): Beschikbaar {
  const gevonden: Beschikbaar = {
    bouwjaar: false,
    woningtype: false,
    buurt: false,
    nieuw: false,
    prijsverlaging: false,
    eerderActief: false,
  };
  for (const feature of features) {
    const eigenschappen = feature.properties;
    if (typeof eigenschappen.bouwjaar === 'number') gevonden.bouwjaar = true;
    if (woningtypeVan(eigenschappen) !== null) gevonden.woningtype = true;
    if (pc4Van(eigenschappen.postcode ?? null) !== null) gevonden.buurt = true;
    if (typeof eigenschappen.badge === 'string' && eigenschappen.badge !== '')
      gevonden.nieuw = true;
    if (eigenschappen.terugkomsten > 0) gevonden.eerderActief = true;
    if (
      typeof eigenschappen.prijsVerlaagd === 'boolean' ||
      (eigenschappen.badge ?? '').toLowerCase().includes('verlaag')
    ) {
      gevonden.prijsverlaging = true;
    }
  }
  return gevonden;
}

export function maakBallon(
  eigenschappen: BallonEigenschappen,
  instellingen: Kaartinstellingen,
  kleurgever: Kleurgever,
  eenheid: Eenheid = 'm2',
): HTMLElement {
  const houder = document.createElement('div');
  houder.className = 'marker';
  houder.style.fontSize = lettergrootte(instellingen.grootte);
  houder.setAttribute('role', 'button');
  houder.setAttribute('tabindex', '0');
  houder.setAttribute('aria-label', ballonOmschrijving(eigenschappen));
  if (isGemarkeerd(eigenschappen, instellingen.markeringen)) {
    houder.setAttribute('data-gemarkeerd', 'true');
  }

  const element = document.createElement('div');
  element.className = 'ballon';
  element.setAttribute('data-vorm', instellingen.vorm);
  houder.append(element);

  const bandkleur = kleurgever(eigenschappen);
  // Het rondje heeft geen ruimte voor een band naast de waarde; daar wordt de
  // kleur de vulling van het rondje zelf.
  if (bandkleur !== null && instellingen.vorm !== 'rond') {
    const band = document.createElement('span');
    band.className = 'ballon-band';
    band.style.backgroundColor = bandkleur;
    element.append(band);
  }
  if (bandkleur !== null && instellingen.vorm === 'rond') {
    element.style.backgroundColor = bandkleur;
    element.style.color = inktOp(bandkleur);
  }

  // Het sterretje van een kansje staat vóór het cijfer, in elke vorm. Krimpt de
  // ballon tot een stip, dan verdwijnt het mee met de rest van de inhoud.
  if (isKansje(eigenschappen)) {
    const ster = document.createElement('span');
    ster.className = 'ballon-ster';
    ster.textContent = '★';
    ster.setAttribute('aria-hidden', 'true');
    element.append(ster);
  }

  // Opnieuw aangeboden (MA-45): een ↻ op dezelfde plek, na het sterretje.
  if (isOpnieuwAangeboden(eigenschappen)) {
    const opnieuw = document.createElement('span');
    opnieuw.className = 'ballon-opnieuw';
    opnieuw.textContent = '↻';
    opnieuw.setAttribute('aria-hidden', 'true');
    element.append(opnieuw);
  }

  const waarde = document.createElement('span');
  waarde.className = 'ballon-waarde';
  waarde.textContent = ballonWaarde(eigenschappen, instellingen.ballon, eenheid);
  element.append(waarde);

  // De m² als eigen vlak (MA-45): alleen het getal, gewoon gewicht, naast het
  // vette cijfer.
  const tweede = ballonTweedeWaarde(eigenschappen, instellingen.ballon, eenheid);
  const metMaat = tweede !== null && instellingen.vorm !== 'rond';
  if (metMaat) {
    const maat = document.createElement('span');
    maat.className = 'ballon-maat';
    maat.textContent = tweede;
    element.append(maat);
  }

  if (toontLabel(instellingen.ballon) && instellingen.vorm !== 'rond') {
    if (!metMaat) {
      const scheiding = document.createElement('span');
      scheiding.className = 'ballon-scheiding';
      scheiding.textContent = '·';
      element.append(scheiding);
    }

    const label = document.createElement('span');
    label.className = 'ballon-label';
    label.textContent = eigenschappen.energielabel ?? '–';
    const vulling = labelVulling(
      eigenschappen.energielabel === null
        ? ENERGIELABEL_ONBEKEND_KLEUR
        : energielabelKleur(eigenschappen.energielabel),
    );
    label.style.backgroundColor = vulling.vlak;
    label.style.color = vulling.inkt;
    element.append(label);
  }

  if (instellingen.vorm === 'druppel') {
    const tip = document.createElement('span');
    tip.className = 'ballon-tip';
    houder.append(tip);
  }

  const onder = tekstOnder(eigenschappen, instellingen.tekst, eenheid);
  if (onder !== null) {
    const regel = document.createElement('span');
    regel.className = 'ballon-onder';
    regel.textContent = onder;
    houder.append(regel);
  }

  return houder;
}

/**
 * A cluster says what the houses under it cost, not how many there are. The
 * range the tint spans is Amsterdam's own: p10-p90 of this set is EUR 5.381 -
 * EUR 10.000, so a ramp from 5k to 13k keeps the middle of the city in the
 * middle of the scale and still has room for the Grachtengordel.
 */
export const CLUSTERSCHAAL_MIN = 5000;
export const CLUSTERSCHAAL_MAX = 13000;

/**
 * Hoeveel woningen onder een cluster elk markeringsmasker dragen. Zestien getallen
 * zijn genoeg om elke combinatie van vinkjes exact te beantwoorden, ook wanneer
 * een woning twee markeringen tegelijk draagt - optellen per markering zou die
 * dan dubbel tellen.
 */
export type Markeringtelling = readonly number[];

export function telMaskers(
  eigenschappen: readonly Partial<BallonEigenschappen>[],
): Markeringtelling {
  const bakjes = new Array<number>(MASKERS).fill(0);
  for (const rij of eigenschappen) {
    const masker = markeringMasker(rij);
    bakjes[masker] = (bakjes[masker] ?? 0) + 1;
  }
  return bakjes;
}

export function gemarkeerdInCluster(
  telling: Markeringtelling,
  markeringen: Kaartinstellingen['markeringen'],
): number {
  const gekozen = gekozenMasker(markeringen);
  if (gekozen === 0) return 0;
  let aantal = 0;
  for (let masker = 0; masker < MASKERS; masker += 1) {
    if ((masker & gekozen) !== 0) aantal += telling[masker] ?? 0;
  }
  return aantal;
}

/** The median of each figure a balloon can show, over the houses in a cluster. */
export type Clustermedianen = {
  prijsPerM2: number | null;
  vraagprijs: number | null;
  oppervlakteM2: number | null;
};

export function mediaan(waarden: readonly number[]): number | null {
  if (waarden.length === 0) return null;
  const gesorteerd = [...waarden].sort((a, b) => a - b);
  const midden = Math.floor(gesorteerd.length / 2);
  if (gesorteerd.length % 2 === 1) return gesorteerd[midden] ?? null;
  const links = gesorteerd[midden - 1] ?? 0;
  const rechts = gesorteerd[midden] ?? 0;
  return Math.round((links + rechts) / 2);
}

function getallen(
  eigenschappen: readonly Partial<BallonEigenschappen>[],
  veld: keyof Clustermedianen,
): number[] {
  const waarden: number[] = [];
  for (const rij of eigenschappen) {
    const waarde = rij[veld];
    if (typeof waarde === 'number' && Number.isFinite(waarde)) waarden.push(waarde);
  }
  return waarden;
}

export function berekenMedianen(
  eigenschappen: readonly Partial<BallonEigenschappen>[],
): Clustermedianen {
  return {
    prijsPerM2: mediaan(getallen(eigenschappen, 'prijsPerM2')),
    vraagprijs: mediaan(getallen(eigenschappen, 'vraagprijs')),
    oppervlakteM2: mediaan(getallen(eigenschappen, 'oppervlakteM2')),
  };
}

/** The cluster's own figure, in the same notation as the balloon's. */
export function clusterWaarde(
  medianen: Clustermedianen,
  welke: Kaartinstellingen['ballon'],
  eenheid: Eenheid = 'm2',
): string {
  switch (welke) {
    case 'vraagprijs':
      return medianen.vraagprijs === null ? '–' : formatEuroCompact(medianen.vraagprijs);
    case 'oppervlakte':
      return formatMaat(medianen.oppervlakteM2, eenheid);
    default:
      return medianen.prijsPerM2 === null
        ? '–'
        : formatEuroCompact(prijsPerEenheid(medianen.prijsPerM2, eenheid));
  }
}

/** Where a median price per m2 sits on the ramp: 0 = cheap, 4 = expensive. */
export function clusterStap(mediaanPrijsPerM2: number | null): number | null {
  if (mediaanPrijsPerM2 === null) return null;
  const deel = (mediaanPrijsPerM2 - CLUSTERSCHAAL_MIN) / (CLUSTERSCHAAL_MAX - CLUSTERSCHAAL_MIN);
  const stap = Math.floor(deel * PRIJSSCHAAL.length);
  return Math.min(PRIJSSCHAAL.length - 1, Math.max(0, stap));
}

/** Background and ink for a cluster, so the figure stays readable on its tint. */
export function clusterTint(mediaanPrijsPerM2: number | null): {
  achtergrond: string;
  tekst: string;
} {
  const stap = clusterStap(mediaanPrijsPerM2);
  if (stap === null) return { achtergrond: '#eceae4', tekst: '#4a4842' };
  const achtergrond = PRIJSSCHAAL[stap] ?? PRIJSSCHAAL[0];
  // Welke inkt het wordt volgt uit de tint, niet uit de stap: wit op de middelste
  // tint (#6f97a6) haalde 3,16:1, terwijl donkere inkt daar 5,5:1 haalt.
  return { achtergrond, tekst: inktOp(achtergrond) };
}

export function clusterStraal(aantal: number, grootte: number): number {
  const grond = aantal < 15 ? 38 : aantal < 50 ? 44 : aantal < 150 ? 50 : 56;
  return Math.round(grond * grootte);
}

/** Wat een bol samenvat: zijn omvang, de medianen eronder, en de verdeling over
 *  de markeringsmaskers. Dat laatste staat los van welke vinkjes aan staan. */
export type Clustergegevens = {
  aantal: number;
  medianen: Clustermedianen;
  markeringen?: Markeringtelling;
};

/**
 * The cluster itself: the median figure in full, the number of houses small
 * underneath - the count is context, not the message.
 */
export function maakCluster(
  gegevens: Clustergegevens,
  instellingen: Kaartinstellingen,
  eenheid: Eenheid = 'm2',
): HTMLElement {
  const element = document.createElement('div');
  element.setAttribute('role', 'button');
  element.setAttribute('tabindex', '0');
  vulCluster(element, gegevens, instellingen, eenheid);
  return element;
}

/**
 * De bol opnieuw invullen, zonder hem eerst weg te halen.
 *
 * Een cluster verschijnt zodra het zoomniveau wisselt, met het aantal dat de
 * bron zelf meestuurt; de mediaan komt een tel later uit de worker en schuift er
 * dan in. Zou de bol op dat antwoord wachten, dan stond er tijdens elke zoom een
 * tel lang niets - en dat gat is precies wat er raar uitziet.
 *
 * `data-stand` houdt bij wat er getekend staat, zodat een ronde die niets
 * verandert ook niets aanraakt: tijdens een zoom loopt dit per beeld opnieuw.
 */
export function vulCluster(
  element: HTMLElement,
  gegevens: Clustergegevens,
  instellingen: Kaartinstellingen,
  eenheid: Eenheid = 'm2',
): void {
  const { aantal, medianen } = gegevens;
  const tint = clusterTint(medianen.prijsPerM2);
  const waarde = clusterWaarde(medianen, instellingen.ballon, eenheid);
  const straal = clusterStraal(aantal, instellingen.grootte);
  const gemarkeerd =
    gegevens.markeringen === undefined
      ? 0
      : gemarkeerdInCluster(gegevens.markeringen, instellingen.markeringen);

  const letter = lettergrootte(instellingen.grootte);
  const stand = `${aantal}|${waarde}|${tint.achtergrond}|${straal}|${letter}|${gemarkeerd}`;
  if (element.dataset.stand === stand) return;
  element.dataset.stand = stand;

  // Toevoegen, niet overschrijven: MapLibre hangt zijn eigen klassen aan de
  // marker (`maplibregl-marker`, en daarmee de absolute plaatsing). Een tweede
  // ronde over een bol die al staat mag die niet wissen.
  element.classList.add('cluster', 'cijfers');
  element.style.width = `${straal}px`;
  element.style.height = `${straal}px`;
  element.style.fontSize = letter;
  element.style.backgroundColor = tint.achtergrond;
  element.style.color = tint.tekst;

  const cijfer = document.createElement('span');
  cijfer.className = 'cluster-waarde';
  cijfer.textContent = waarde;

  const telling = document.createElement('span');
  telling.className = 'cluster-aantal';
  telling.textContent = String(aantal);

  const delen: HTMLElement[] = [cijfer, telling];

  // Een markering die op stadsniveau verdwijnt is geen markering. Draagt een
  // cluster gemarkeerde woningen, dan krijgt de bol dezelfde ring als de ballon
  // en staat erbij hoeveel van zijn woningen het betreft.
  if (gemarkeerd > 0) {
    element.setAttribute('data-gemarkeerd', 'true');
    const merk = document.createElement('span');
    merk.className = 'cluster-markering cijfers';
    merk.textContent = String(gemarkeerd);
    delen.push(merk);
  } else {
    element.removeAttribute('data-gemarkeerd');
  }
  element.replaceChildren(...delen);

  element.setAttribute(
    'aria-label',
    `${aantal} woningen, mediaan ${waarde.replace('–', 'onbekend')}${
      gemarkeerd > 0 ? `, ${gemarkeerd} gemarkeerd` : ''
    }`,
  );
}
